import React, { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { Form, FormGroup, Label, Input, Button, Alert, Container, Card, CardBody, CardHeader } from '../shim/reactstrap';
import ReCAPTCHA from 'react-google-recaptcha';
import { useAuth } from '../contexts/AuthContext';

export const SignUp = () => {
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const recaptchaRef = useRef(null);

  const navigate = useNavigate();
  const { apiClient, setUser } = useAuth();

  // Replace with your actual Google reCAPTCHA site key
  // Get this from https://www.google.com/recaptcha/admin
  const RECAPTCHA_SITE_KEY = process.env.REACT_APP_RECAPTCHA_SITE_KEY || '6LeIxAcTAAAAAJcZVRqyHh71UMIEGNQ_MXjiZKhI'; // Test key

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');
    setIsSubmitting(true);

    try {
      // Validate passwords match
      if (password !== passwordConfirm) {
        throw new Error('Passwords do not match');
      }

      // Get reCAPTCHA token
      let recaptchaToken = null;
      if (recaptchaRef.current) {
        recaptchaToken = recaptchaRef.current.getValue();
        if (!recaptchaToken) {
          throw new Error('Please complete the captcha verification');
        }
      }

      // Call registration API
      const userData = await apiClient.register(email, name, password, passwordConfirm, recaptchaToken);

      // Set user in auth context (user is auto-logged in after registration)
      setUser(userData);

      setSuccess('Registration successful! Redirecting...');
      setTimeout(() => {
        navigate('/', { replace: true });
      }, 1500);
    } catch (err) {
      setError(err.message || 'Registration failed. Please try again.');
      // Reset reCAPTCHA on error
      if (recaptchaRef.current) {
        recaptchaRef.current.reset();
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Container className="mt-5">
      <div className="row justify-content-center">
        <div className="col-md-6 col-lg-5">
          <Card>
            <CardHeader>
              <h4 className="mb-0">Sign Up for WaveWise</h4>
            </CardHeader>
            <CardBody>
              {error && <Alert color="danger" fade={false}>{error}</Alert>}
              {success && <Alert color="success" fade={false}>{success}</Alert>}
              <Form onSubmit={handleSubmit}>
                <FormGroup>
                  <Label for="email">Email *</Label>
                  <Input
                    type="email"
                    id="email"
                    name="email"
                    placeholder="Enter your email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    autoComplete="email"
                    disabled={isSubmitting}
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="name">Full Name *</Label>
                  <Input
                    type="text"
                    id="name"
                    name="name"
                    placeholder="Enter your full name"
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    required
                    autoComplete="name"
                    disabled={isSubmitting}
                  />
                </FormGroup>
                <FormGroup>
                  <Label for="password">Password *</Label>
                  <Input
                    type="password"
                    id="password"
                    name="password"
                    placeholder="Enter your password (min 8 characters)"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    disabled={isSubmitting}
                  />
                  <small className="form-text text-muted">
                    Password must be at least 8 characters long
                  </small>
                </FormGroup>
                <FormGroup>
                  <Label for="passwordConfirm">Confirm Password *</Label>
                  <Input
                    type="password"
                    id="passwordConfirm"
                    name="passwordConfirm"
                    placeholder="Confirm your password"
                    value={passwordConfirm}
                    onChange={(e) => setPasswordConfirm(e.target.value)}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    disabled={isSubmitting}
                  />
                </FormGroup>
                <FormGroup className="d-flex justify-content-center">
                  <ReCAPTCHA
                    ref={recaptchaRef}
                    sitekey={RECAPTCHA_SITE_KEY}
                  />
                </FormGroup>
                <Button color="primary" type="submit" block className="mt-3" disabled={isSubmitting}>
                  {isSubmitting ? 'Creating Account...' : 'Sign Up'}
                </Button>
                <div className="text-center mt-3">
                  <small>
                    Already have an account?{' '}
                    <a href="/login" onClick={(e) => { e.preventDefault(); navigate('/login'); }}>
                      Login here
                    </a>
                  </small>
                </div>
              </Form>
            </CardBody>
          </Card>
        </div>
      </div>
    </Container>
  );
};
